/**
 * Digest Service
 * Client-side service for communicating with the digest API
 */

import { protectedResources } from '../config/msalConfig';

const API_BASE = protectedResources.api.baseEndPoint;

/**
 * Get user's digest preferences
 */
export async function getPreferences(accessToken) {
    try {
        const response = await fetch(`${API_BASE}/v1/digest/preferences`, {
            method: 'GET',
            headers: {
                'Authorization': `Bearer ${accessToken}`,
                'Content-Type': 'application/json'
            }
        });

        if (!response.ok) {
            throw new Error(`Failed to fetch preferences: ${response.statusText}`);
        }

        const data = await response.json();
        return data;
    } catch (error) {
        console.error('Error fetching preferences:', error);
        throw error;
    }
}

/**
 * Update user's digest preferences
 */
export async function updatePreferences(preferences, accessToken) {
    try {
        const response = await fetch(`${API_BASE}/v1/digest/preferences`, {
            method: 'PUT',
            headers: {
                'Authorization': `Bearer ${accessToken}`,
                'Content-Type': 'application/json'
            },
            body: JSON.stringify(preferences)
        });

        if (!response.ok) {
            throw new Error(`Failed to update preferences: ${response.statusText}`);
        }

        const data = await response.json();
        return data;
    } catch (error) {
        console.error('Error updating preferences:', error);
        throw error;
    }
}

/**
 * Get user's subscriptions
 */
export async function getSubscriptions(accessToken) {
    try {
        const response = await fetch(`${API_BASE}/v1/digest/subscriptions`, {
            method: 'GET',
            headers: {
                'Authorization': `Bearer ${accessToken}`,
                'Content-Type': 'application/json'
            }
        });

        if (!response.ok) {
            throw new Error(`Failed to fetch subscriptions: ${response.statusText}`);
        }

        const data = await response.json();
        return data;
    } catch (error) {
        console.error('Error fetching subscriptions:', error);
        throw error;
    }
}

/**
 * Get available modules for subscription
 */
export async function getAvailableModules(accessToken) {
    try {
        const response = await fetch(`${API_BASE}/v1/digest/modules`, {
            method: 'GET',
            headers: {
                'Authorization': `Bearer ${accessToken}`,
                'Content-Type': 'application/json'
            }
        });

        if (!response.ok) {
            throw new Error(`Failed to fetch available modules: ${response.statusText}`);
        }

        const data = await response.json();
        return data;
    } catch (error) {
        console.error('Error fetching available modules:', error);
        throw error;
    }
}

/**
 * Subscribe to a module
 */
export async function subscribeToModule(moduleId, subscription, accessToken) {
    try {
        const response = await fetch(`${API_BASE}/v1/digest/subscribe`, {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${accessToken}`,
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({
                moduleId,
                ...subscription
            })
        });

        if (!response.ok) {
            throw new Error(`Failed to subscribe: ${response.statusText}`);
        }

        const data = await response.json();
        return data;
    } catch (error) {
        console.error('Error subscribing to module:', error);
        throw error;
    }
}

/**
 * Update subscription
 */
export async function updateSubscription(subscriptionId, updates, accessToken) {
    try {
        const response = await fetch(`${API_BASE}/v1/digest/subscription/${subscriptionId}`, {
            method: 'PUT',
            headers: {
                'Authorization': `Bearer ${accessToken}`,
                'Content-Type': 'application/json'
            },
            body: JSON.stringify(updates)
        });

        if (!response.ok) {
            throw new Error(`Failed to update subscription: ${response.statusText}`);
        }

        const data = await response.json();
        return data;
    } catch (error) {
        console.error('Error updating subscription:', error);
        throw error;
    }
}

/**
 * Unsubscribe from module
 */
export async function unsubscribeFromModule(moduleId, accessToken) {
    try {
        const response = await fetch(`${API_BASE}/v1/digest/unsubscribe/${moduleId}`, {
            method: 'DELETE',
            headers: {
                'Authorization': `Bearer ${accessToken}`,
                'Content-Type': 'application/json'
            }
        });

        if (!response.ok) {
            throw new Error(`Failed to unsubscribe: ${response.statusText}`);
        }

        const data = await response.json();
        return data;
    } catch (error) {
        console.error('Error unsubscribing:', error);
        throw error;
    }
}

/**
 * Get delivery history
 */
export async function getDeliveryHistory(limit = 50, accessToken) {
    try {
        const response = await fetch(`${API_BASE}/v1/digest/history?limit=${limit}`, {
            method: 'GET',
            headers: {
                'Authorization': `Bearer ${accessToken}`,
                'Content-Type': 'application/json'
            }
        });

        if (!response.ok) {
            throw new Error(`Failed to fetch delivery history: ${response.statusText}`);
        }

        const data = await response.json();
        return data;
    } catch (error) {
        console.error('Error fetching delivery history:', error);
        throw error;
    }
}

/**
 * Trigger manual digest
 */
export async function triggerManualDigest(moduleId, recipientEmail, accessToken, forceRefresh = false) {
    try {
        const response = await fetch(`${API_BASE}/v1/digest/trigger/${moduleId}`, {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${accessToken}`,
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({
                email: recipientEmail,
                forceRefresh: forceRefresh
            })
        });

        if (!response.ok) {
            throw new Error(`Failed to trigger digest: ${response.statusText}`);
        }

        const data = await response.json();
        return data;
    } catch (error) {
        console.error('Error triggering digest:', error);
        throw error;
    }
}

/**
 * Get queue statistics
 */
export async function getQueueStats(accessToken) {
    try {
        const response = await fetch(`${API_BASE}/v1/digest/stats/queue`, {
            method: 'GET',
            headers: {
                'Authorization': `Bearer ${accessToken}`,
                'Content-Type': 'application/json'
            }
        });

        if (!response.ok) {
            throw new Error(`Failed to fetch queue stats: ${response.statusText}`);
        }

        const data = await response.json();
        return data;
    } catch (error) {
        console.error('Error fetching queue stats:', error);
        throw error;
    }
}

/**
 * Get module health scores for all accessible modules
 */
export async function getModuleHealthScores(accessToken) {
    try {
        const response = await fetch(`${API_BASE}/v1/digest/module-health`, {
            method: 'GET',
            headers: {
                'Authorization': `Bearer ${accessToken}`,
                'Content-Type': 'application/json'
            }
        });

        if (!response.ok) {
            throw new Error(`Failed to fetch module health scores: ${response.statusText}`);
        }

        const data = await response.json();
        return data;
    } catch (error) {
        console.error('Error fetching module health scores:', error);
        throw error;
    }
}

/**
 * Get module digest (latest active summary)
 * Retrieve cached module digest without regenerating
 */
export async function getModuleDigest(moduleId, accessToken) {
    try {
        const response = await fetch(`${API_BASE}/v1/digest/module/${moduleId}`, {
            method: 'GET',
            headers: {
                'Authorization': `Bearer ${accessToken}`,
                'Content-Type': 'application/json'
            }
        });

        if (!response.ok) {
            if (response.status === 404) {
                return { success: false, notFound: true };
            }
            if (response.status === 403) {
                throw new Error('Access denied to this module');
            }
            throw new Error(`Failed to fetch module digest: ${response.statusText}`);
        }

        const data = await response.json();
        return data;
    } catch (error) {
        console.error('Error fetching module digest:', error);
        throw error;
    }
}

/**
 * Generate AI-powered visual dashboard for a module
 * Uses Azure AI to transform digest data into professional dashboard visualization
 */
export async function generateModuleDashboard(moduleId, accessToken) {
    try {
        const response = await fetch(`${API_BASE}/v1/dashboard/module/${moduleId}`, {
            method: 'GET',
            headers: {
                'Authorization': `Bearer ${accessToken}`,
                'Content-Type': 'application/json'
            }
        });

        if (!response.ok) {
            if (response.status === 404) {
                return { success: false, notFound: true, error: 'Module digest not found. Please generate a digest first.' };
            }
            if (response.status === 403) {
                throw new Error('Access denied to this module');
            }
            throw new Error(`Failed to generate dashboard: ${response.statusText}`);
        }

        const data = await response.json();
        return data;
    } catch (error) {
        console.error('Error generating module dashboard:', error);
        throw error;
    }
}

/**
 * Generate AI-powered visual dashboard for a page (with caching)
 * Uses Azure AI to transform page summary data into professional dashboard visualization
 * Returns cached dashboard if available, otherwise generates new one
 */
export async function generatePageDashboard(reportId, pageId, accessToken) {
    try {
        const response = await fetch(`${API_BASE}/v1/dashboard/page/${reportId}/${pageId}`, {
            method: 'GET',
            headers: {
                'Authorization': `Bearer ${accessToken}`,
                'Content-Type': 'application/json'
            }
        });

        if (!response.ok) {
            if (response.status === 404) {
                return { success: false, notFound: true, error: 'Page summary not found. Please generate a summary first.' };
            }
            if (response.status === 403) {
                throw new Error('Access denied to this page');
            }
            throw new Error(`Failed to generate dashboard: ${response.statusText}`);
        }

        const data = await response.json();
        return data;
    } catch (error) {
        console.error('Error generating page dashboard:', error);
        throw error;
    }
}

/**
 * Regenerate page dashboard (force refresh, clear cache)
 * Forces generation of a new dashboard, bypassing cache
 */
export async function regeneratePageDashboard(reportId, pageId, accessToken) {
    try {
        const response = await fetch(`${API_BASE}/v1/dashboard/page/${reportId}/${pageId}/regenerate`, {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${accessToken}`,
                'Content-Type': 'application/json'
            }
        });

        if (!response.ok) {
            if (response.status === 404) {
                return { success: false, notFound: true, error: 'Page summary not found. Please generate a summary first.' };
            }
            if (response.status === 403) {
                throw new Error('Access denied to this page');
            }
            throw new Error(`Failed to regenerate dashboard: ${response.statusText}`);
        }

        const data = await response.json();
        return data;
    } catch (error) {
        console.error('Error regenerating page dashboard:', error);
        throw error;
    }
}

/**
 * Get comprehensive module intelligence data
 * Returns detailed module breakdown with insights, metrics, and rankings
 */
export async function getModuleIntelligence(organizationId, accessToken) {
    try {
        const response = await fetch(`${API_BASE}/v1/dashboard/module-intelligence?organizationId=${organizationId}`, {
            method: 'GET',
            headers: {
                'Authorization': `Bearer ${accessToken}`,
                'Content-Type': 'application/json'
            }
        });

        if (!response.ok) {
            throw new Error(`Failed to fetch module intelligence: ${response.statusText}`);
        }

        const data = await response.json();
        return data;
    } catch (error) {
        console.error('Error fetching module intelligence:', error);
        throw error;
    }
}
